import { Check, Radio, Zap } from 'lucide-react';

import { cn } from '@/shared/utils/utils';
import confluenceLogo from '@/features/knowledge/assets/confluence.svg';

import type { RoiIntegrationCoverage, RoiSignalSource } from '../types/roi';

interface RoiIntegrationToggleBarProps {
  coverage: RoiIntegrationCoverage;
  enabledSources: ReadonlySet<RoiSignalSource>;
  onToggle: (source: RoiSignalSource) => void;
  currentRecallPct: number;
  fullRecallPct: number;
}

const SOURCE_LOGO: Record<RoiSignalSource, string | null> = {
  jira: '/jira-logo.svg',
  linear: '/linear-logo.svg',
  monday: '/monday-logo.svg',
  asana: '/asana-logo.svg',
  github: '/github-logo.svg',
  slack: '/slack-logo.svg',
  teams: '/teams-logo.svg',
  confluence: confluenceLogo,
  notion: '/notion-logo.svg',
  sharepoint: '/sharepoint-logo.svg',
  gdrive: '/gdrive-logo.svg',
  pagerduty: null,
  salesforce: '/salesforce-logo.svg',
  hubspot: '/hubspot-logo.svg',
};

interface EvidenceQuote {
  artifact: string;
  quote: string;
  context: string;
}

const EVIDENCE_BY_SOURCE: Record<RoiSignalSource, EvidenceQuote> = {
  jira: {
    artifact: 'PAY-9281',
    quote: 'Reopened 3× in 12 days · scope edits each time',
    context: 'Payments idempotency hardening',
  },
  github: {
    artifact: 'PR #21044',
    quote: 'Full revert two weeks after merge',
    context: 'iOS background sync overhaul',
  },
  slack: {
    artifact: '#payments-eng',
    quote: '"rolling back webhook dedupe" - 4 engineers, 2 hours',
    context: 'Validates the rework signature on PAY-9281',
  },
  linear: {
    artifact: 'SRCH-1402',
    quote: 'Cycle re-entered "In progress" 3× post-planning',
    context: 'Search re-ranking v2',
  },
  pagerduty: {
    artifact: 'INC-2041',
    quote: 'P1 nine days post-launch · duplicate charge bug',
    context: 'Linked back to unresolved rework signature',
  },
  confluence: {
    artifact: 'Search Re-ranking PRD',
    quote: 'PRD edited 3× after engineering kickoff',
    context: 'Scope drift between plan and tickets',
  },
  teams: {
    artifact: 'teams/risk/escalation',
    quote: 'Cross-team escalation 6 days before planned ship',
    context: 'Risk model retraining pipeline',
  },
  notion: {
    artifact: 'notion/payments-roadmap',
    quote: 'Roadmap churned 7× after kickoff',
    context: 'Late "open questions" added mid-build',
  },
  sharepoint: {
    artifact: 'sp:storage/PRD-v4',
    quote: 'Spec branched into 3 parallel versions',
    context: 'Postgres sharding strategy',
  },
  gdrive: {
    artifact: 'gdrive/mobile-design.fig',
    quote: '23 comments in the week scope changed',
    context: 'iOS background sync overhaul',
  },
  monday: {
    artifact: 'MOB-board/127',
    quote: 'Owner reassigned 2× mid-sprint',
    context: 'Cross-team handoff caused rework',
  },
  asana: {
    artifact: 'GROWTH-task/362',
    quote: 'Due date pushed 4× before reaching done',
    context: 'Onboarding email sequence v3',
  },
  salesforce: {
    artifact: 'SF-CASE-4291',
    quote: 'Customer-commit case escalated · ship slipped past contract',
    context: 'Marketplace payouts overhaul',
  },
  hubspot: {
    artifact: 'HS-TICKET-5118',
    quote: 'Renewal-risk flag traced to post-launch defect surge',
    context: 'Saved-card vault migration',
  },
};

interface SourceLogoProps {
  source: RoiSignalSource;
  enabled: boolean;
}

function SourceLogo({ source, enabled }: SourceLogoProps) {
  const logoUrl = SOURCE_LOGO[source];
  const containerClass = cn(
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-md border bg-white transition-all',
    enabled
      ? 'border-gray-200 shadow-[0_1px_0_rgba(15,23,42,0.04)]'
      : 'border-gray-200 opacity-40 grayscale'
  );
  if (logoUrl) {
    return (
      <span className={containerClass}>
        <img src={logoUrl} alt="" className="h-4 w-4" />
      </span>
    );
  }
  return (
    <span className={containerClass}>
      <Zap className="h-3.5 w-3.5 text-[#06ac38]" />
    </span>
  );
}

export function RoiIntegrationToggleBar({
  coverage,
  enabledSources,
  onToggle,
  currentRecallPct,
  fullRecallPct,
}: RoiIntegrationToggleBarProps) {
  const enabledSteps = coverage.steps.filter((step) =>
    enabledSources.has(step.source)
  );
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          Connected sources
        </span>
        <div className="flex items-baseline gap-2 tabular-nums">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Recall
          </span>
          <span className="text-sm font-semibold text-slate-900">
            {currentRecallPct}%
          </span>
          <span className="text-[11px] text-slate-400">/ {fullRecallPct}%</span>
        </div>
      </header>
      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7">
        {coverage.steps.map((step) => {
          const isEnabled = enabledSources.has(step.source);
          return (
            <button
              key={step.source}
              type="button"
              onClick={() => onToggle(step.source)}
              aria-pressed={isEnabled}
              className={cn(
                'group flex items-center gap-2 rounded-lg border px-2.5 py-2 text-start transition-all',
                isEnabled
                  ? 'border-[rgb(0,115,234)]/30 bg-[rgb(0,115,234)]/[0.04]'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              )}
            >
              <SourceLogo source={step.source} enabled={isEnabled} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    'truncate text-[12px] font-semibold leading-tight',
                    isEnabled ? 'text-slate-900' : 'text-slate-500'
                  )}
                >
                  {step.label}
                </span>
                <span
                  className={cn(
                    'text-[10px] tabular-nums leading-tight',
                    isEnabled ? 'text-[rgb(0,115,234)]' : 'text-slate-400'
                  )}
                >
                  +{step.recallDeltaPct}% recall
                </span>
              </span>
              <span
                className={cn(
                  'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border transition-colors',
                  isEnabled
                    ? 'border-[rgb(0,115,234)] bg-[rgb(0,115,234)] text-white'
                    : 'border-slate-300 bg-white text-transparent'
                )}
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3.5} />
              </span>
            </button>
          );
        })}
      </div>

      {enabledSteps.length > 0 && (
        <div className="border-t border-gray-100 bg-slate-50/40 px-5 py-3">
          <div className="mb-2 flex items-center gap-1.5">
            <Radio className="h-3 w-3 animate-pulse text-emerald-500" />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
              Signals now in view ({enabledSteps.length})
            </span>
          </div>
          <ul className="grid grid-cols-1 gap-1.5 md:grid-cols-2 lg:grid-cols-3">
            {enabledSteps.map((step) => {
              const evidence = EVIDENCE_BY_SOURCE[step.source];
              return (
                <li
                  key={step.source}
                  className="flex items-start gap-2 rounded-md border border-slate-200/70 bg-white px-2.5 py-1.5 text-[11px]"
                >
                  <SourceLogo source={step.source} enabled />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                        {step.label}
                      </span>
                      <span className="truncate text-[10px] tabular-nums text-slate-400">
                        {evidence.artifact}
                      </span>
                    </div>
                    <div className="mt-0.5 truncate text-[11px] font-medium text-slate-700">
                      {evidence.quote}
                    </div>
                    <div className="truncate text-[10px] text-slate-400">
                      {evidence.context}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
